import { FormBuilder, FormBuilderHandle } from '@/features/form_builder';
import { useSubmissionTemplateVersion } from '@/hooks/submission_forms/useSubmissionTemplateVersion';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlertCircle, Save } from 'lucide-react';
import { FormTreeData } from '@/features/submission_forms/types';
import { SubmissionTemplateVersionData } from '@/hooks/submission_forms/useSubmissionTemplateVersion';
import ReviewChangesDialog from './components/ReviewChangesDialog';
import { diffTotal } from './components/VersionDiff';

interface EditorPanelProps {
  templateId: string;
  onPublished?: () => void;
}

// Tab content for the "Editor" tab of the template workspace — this is the
// former standalone Edit.tsx page body, unchanged internally, minus its own
// page chrome (breadcrumb/back-link now live in the shared Workspace shell).
const EditorPanel: React.FC<EditorPanelProps> = ({ templateId, onPublished }) => {
  const {
    openOrLoadDraft,
    saveDraftTree,
    publishVersion,
    discardDraft,
    listVersions,
    compareVersions,
  } = useSubmissionTemplateVersion();

  const [draft, setDraft] = useState<SubmissionTemplateVersionData | null>(
    null
  );
  const [publishedVersion, setPublishedVersion] =
    useState<SubmissionTemplateVersionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  // Whether the draft's saved tree actually differs from the published
  // version — distinct from isDirty (FormBuilder's own unsaved local
  // edits). Discard should only be clickable when there's genuinely
  // something to throw away, saved or not.
  const [hasChangesFromPublished, setHasChangesFromPublished] = useState(false);
  // True when the last refreshChangeStatus call failed to reach the server —
  // distinct from hasChangesFromPublished so a transient failure doesn't get
  // treated as a confirmed "no changes," which would permanently disable
  // Publish until another edit+save cycle happened to succeed.
  const [changeStatusError, setChangeStatusError] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  // Bumped after a successful discard to force FormBuilder to remount —
  // discard resets the draft's tree in place (same draft.id), so FormBuilder
  // would otherwise keep showing its own stale local `sections` state.
  const [resetCount, setResetCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formBuilderRef = useRef<FormBuilderHandle>(null);
  // True once this draft has been published in this session — the backend
  // freezes a published version (see version_must_be_editable), so further
  // edits/saves here would start failing silently unless the user opens a
  // new draft. Rather than teach FormBuilder a read-only mode, just tell
  // them plainly and offer the one action that unblocks them.
  const [justPublished, setJustPublished] = useState(false);

  // Refreshes whether the draft (as last saved) differs from the published
  // version — a template that's never been published has nothing to
  // discard back to, so it's always treated as "no changes" here.
  const refreshChangeStatus = useCallback(
    async (publishedId: string | undefined, draftId: string) => {
      if (!publishedId) {
        setHasChangesFromPublished(false);
        setChangeStatusError(false);
        return;
      }
      try {
        const comparison = await compareVersions(publishedId, draftId);
        setHasChangesFromPublished(diffTotal(comparison) > 0);
        setChangeStatusError(false);
      } catch {
        // Don't guess hasChangesFromPublished — but do flag the failure so
        // callers can avoid treating a stale/unknown value as "no changes"
        // and permanently blocking Publish.
        setChangeStatusError(true);
      }
    },
    [compareVersions]
  );

  const loadDraft = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setJustPublished(false);
    try {
      const [version, allVersions] = await Promise.all([
        openOrLoadDraft(templateId),
        listVersions(templateId),
      ]);
      setDraft(version);
      const published = allVersions.find(v => v.status === 'published') ?? null;
      setPublishedVersion(published);
      await refreshChangeStatus(published?.id, version.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load draft');
    } finally {
      setIsLoading(false);
    }
  }, [templateId, openOrLoadDraft, listVersions, refreshChangeStatus]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const handleSave = async (sections: FormTreeData[]) => {
    if (!draft) return;
    try {
      const updated = await saveDraftTree(templateId, draft.id, sections);
      setDraft(updated);
      await refreshChangeStatus(publishedVersion?.id, updated.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
      throw err;
    }
  };

  const handleSaveClick = () => {
    // handleSave (passed to FormBuilder as onSave) already surfaces failures
    // via the `error` state rendered above — this catch just prevents an
    // unhandled promise rejection, it's not swallowing anything unseen.
    formBuilderRef.current?.save().catch(() => {});
  };

  const handlePublish = async () => {
    if (!draft) return;
    setIsPublishing(true);
    setError(null);
    try {
      const published = await publishVersion(draft.id);
      setDraft(published);
      setJustPublished(true);
      onPublished?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
      throw err;
    } finally {
      setIsPublishing(false);
    }
  };

  const handleStartNewDraft = () => {
    loadDraft();
  };

  const handleDiscard = async () => {
    if (!draft) return;
    if (
      !confirm(
        publishedVersion
          ? `Discard all changes in this draft and reset it back to the currently live v${publishedVersion.version_number}?`
          : 'Discard all changes in this draft and reset it back to empty?'
      )
    ) {
      return;
    }
    setIsDiscarding(true);
    setError(null);
    try {
      const reset = await discardDraft(draft.id);
      setDraft(reset);
      setResetCount(c => c + 1);
      setHasChangesFromPublished(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard changes');
    } finally {
      setIsDiscarding(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-gray-600">Loading form builder...</div>
      </div>
    );
  }

  if (error && !draft) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const initialSections = draft?.question_tree || [];
  // Only block Publish on "nothing changed" once there's an actual published
  // baseline to compare against — a template's first-ever publish always has
  // something meaningful to publish, even though hasChangesFromPublished is
  // (correctly) false when there's no publishedVersion to diff against. Also
  // never block on a stale/unknown value from a failed comparison fetch —
  // that would leave Publish permanently disabled after a transient error.
  const noChangesToPublish = !!publishedVersion && !hasChangesFromPublished && !changeStatusError;
  // Mirrors noChangesToPublish for Discard: with no published baseline, a
  // never-published draft with real content still has something to discard
  // (reset back to empty) even though hasChangesFromPublished is false.
  const draftHasAnyContent = initialSections.length > 0;
  const nothingToDiscard = publishedVersion
    ? !hasChangesFromPublished && !changeStatusError
    : !draftHasAnyContent;

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)]">
      <div className="bg-white border-b px-5 py-2.5 flex items-center justify-between shrink-0">
        <div className="text-xs text-gray-500">
          Editing draft <span className="font-mono">v{draft?.version_number}</span>
          {isDirty && <span className="ml-2 text-amber-600">Unsaved changes</span>}
          {error && <span className="ml-2 text-red-600">{error}</span>}
          {!error && changeStatusError && draft && (
            <span className="ml-2 text-amber-600">
              Couldn&apos;t check for changes —{' '}
              <button
                type="button"
                onClick={() => refreshChangeStatus(publishedVersion?.id, draft.id)}
                className="underline hover:no-underline"
              >
                retry
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleDiscard}
            disabled={
              isDiscarding ||
              !draft ||
              justPublished ||
              (!isDirty && nothingToDiscard)
            }
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            {isDiscarding ? 'Discarding…' : 'Discard changes'}
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={isSaving || !isDirty || !draft || justPublished}
            variant="outline"
            size="sm"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={isDirty || noChangesToPublish ? 0 : undefined}>
                  <Button
                    onClick={() => setShowReviewDialog(true)}
                    disabled={
                      isPublishing ||
                      isDirty ||
                      !draft ||
                      justPublished ||
                      noChangesToPublish
                    }
                    size="sm"
                  >
                    {isPublishing ? 'Publishing…' : 'Review & Publish'}
                  </Button>
                </span>
              </TooltipTrigger>
              {isDirty && (
                <TooltipContent>Save your changes before publishing</TooltipContent>
              )}
              {!isDirty && noChangesToPublish && (
                <TooltipContent>
                  No changes to publish — this draft matches the live version
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      {justPublished && (
        <div className="bg-green-50 border-b border-green-200 px-5 py-2.5 flex items-center justify-between shrink-0 text-xs text-green-800">
          <span>
            Published as version {draft?.version_number}. This version is now
            locked — start a new draft to keep editing.
          </span>
          <Button size="sm" variant="outline" onClick={handleStartNewDraft}>
            Start new draft
          </Button>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <FormBuilder
          ref={formBuilderRef}
          key={`${draft?.id}:${resetCount}`}
          initialSections={initialSections}
          onSave={handleSave}
          onDirtyChange={setIsDirty}
          onSavingChange={setIsSaving}
        />
      </div>

      {draft && (
        <ReviewChangesDialog
          open={showReviewDialog}
          onOpenChange={setShowReviewDialog}
          title="Review changes before publishing"
          description={
            publishedVersion
              ? `Publishing will replace the currently live v${publishedVersion.version_number} with this draft. Here's what will change:`
              : "This template has no published version yet — publishing will make this draft the live version."
          }
          confirmLabel="Publish"
          confirmingLabel="Publishing…"
          loadComparison={
            publishedVersion
              ? () => compareVersions(publishedVersion.id, draft.id)
              : undefined
          }
          onConfirm={handlePublish}
        />
      )}
    </div>
  );
};

export default EditorPanel;
